import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection, doc, getDocs, addDoc, deleteDoc, serverTimestamp,
  query, where, writeBatch, orderBy, updateDoc, arrayUnion, arrayRemove,
} from "firebase/firestore";
import { SIM_CATALOG } from "./FacultyClassroom";
import { smartSortFiles } from "./utils/fileOrder";

/* =========================================================================
   📡 Broadcast Panel — admin pushes a reading material OR quiz so it
   appears in every faculty class at a chosen Level (PUC-I, PUC-II,
   Engineering Physics) — or in every class everywhere.

   Storage model:
     files / quizzes documents get a `broadcastLevel` field:
       "PUC-I" | "PUC-II" | "Engineering Physics" | "All"
     They have NO facultyClassId (null), so the per-class TabReading and
     TabQuizzes views must read BOTH:
        (a) facultyClassId == this.id
        (b) broadcastLevel in [this.level, "All"]
   ========================================================================= */

const LEVELS = ["PUC-I", "PUC-II", "Engineering Physics", "All"];
const LEVEL_BG = {
  "PUC-I":               { bg: "#e3f2fd", color: "#1565c0" },
  "PUC-II":              { bg: "#e8f5e9", color: "#1b5e20" },
  "Engineering Physics": { bg: "#f3e5f5", color: "#6a1b9a" },
  "All":                 { bg: "#fff3e0", color: "#e65100" },
};

export default function BroadcastPanel({ user }) {
  const [tab, setTab] = useState("share");
  return (
    <div style={{ padding: "10px 0" }}>
      <h2 style={{ color: "#1a237e", margin: "0 0 4px" }}>📡 Broadcast Content</h2>
      <p style={{ color: "#37474f", fontWeight: 500, margin: "0 0 18px", fontSize: "0.92rem" }}>
        Push a reading material or a quiz to <strong>every faculty class at the chosen Level</strong> at once —
        no need to upload separately in each class.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { id: "share",      label: "🎯 Share to Classes" },
          { id: "reading",    label: "📖 Reading Materials" },
          { id: "assignment", label: "📋 Assignments" },
          { id: "quiz",       label: "📝 Quizzes" },
          { id: "simulation", label: "🔬 Simulations" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: tab === t.id ? "#1a237e" : "#fff",
            color: tab === t.id ? "#fff" : "#1a237e",
            border: "1.5px solid #1a237e", borderRadius: 8,
            padding: "8px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.9rem"
          }}>{t.label}</button>
        ))}
      </div>

      {tab === "share"      && <ShareToClasses      user={user} />}
      {tab === "reading"    && <BroadcastReading    user={user} />}
      {tab === "assignment" && <BroadcastAssignment user={user} />}
      {tab === "quiz"       && <BroadcastQuiz       user={user} />}
      {tab === "simulation" && <BroadcastSimulation user={user} />}
    </div>
  );
}

/* ───────────────────────── 🎯 Share to selected classes ─────────────────────────
   Copies reading materials / assignments into the chosen classrooms
   (files docs with facultyClassId), so they show in each class's tab.
   Every copy carries shareBatchId so a whole share can be undone at once.
   ───────────────────────────────────────────────────────────────────────────── */
function ShareToClasses({ user }) {
  const [kind, setKind] = useState("Reading Materials");      // or "Assignments"
  const [mode, setMode] = useState("library");                 // "library" | "link"
  const [libSource, setLibSource] = useState("Engineering Physics");
  const [libItems, setLibItems] = useState([]);
  const [libPicked, setLibPicked] = useState({});
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [classes, setClasses] = useState([]);
  const [picked, setPicked] = useState({});
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [onlineList, setOnlineList] = useState([]);
  const [onlinePick, setOnlinePick] = useState("");
  const isOnline = kind === "Online Class";

  const loadOnline = async () => {
    const snap = await getDocs(query(collection(db, "onlineClasses"), orderBy("scheduledTime", "desc")));
    const now = Date.now();
    setOnlineList(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => {
      if (c.isActive) return true;
      if (c.endedAt) return false;
      return new Date(c.scheduledTime).getTime() + (Number(c.duration) || 60) * 60000 >= now;
    }));
  };

  const loadClasses = async () => {
    const snap = await getDocs(collection(db, "facultyClasses"));
    setClasses(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name))));
  };
  const loadLibrary = async () => {
    setLibPicked({});
    const snap = await getDocs(query(collection(db, "files"), where("class", "==", libSource)));
    setLibItems(smartSortFiles(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .filter(f => (f.section || "Reading Materials") === kind)));
  };
  const loadHistory = async () => {
    const snap = await getDocs(query(collection(db, "files"), where("shareBatchId", "!=", null)));
    const groups = {};
    snap.docs.forEach(d => {
      const f = { id: d.id, ...d.data() };
      const g = groups[f.shareBatchId] || (groups[f.shareBatchId] = { id: f.shareBatchId, ids: [], names: new Set(), classIds: new Set(), section: f.section, at: f.createdAt?.seconds || 0 });
      g.ids.push(f.id); g.names.add(f.name); g.classIds.add(f.facultyClassId);
    });
    setHistory(Object.values(groups).sort((a, b) => b.at - a.at));
  };
  useEffect(() => { loadClasses(); loadHistory(); loadOnline(); }, []);
  useEffect(() => { if (mode === "library" && !isOnline) loadLibrary(); }, [mode, libSource, kind]);

  const shareOnline = async (remove) => {
    if (!onlinePick) { alert("Choose an online class."); return; }
    if (pickedClassIds.length === 0) { alert("Choose at least one class."); return; }
    setBusy(true);
    try {
      await updateDoc(doc(db, "onlineClasses", onlinePick), {
        classIds: remove ? arrayRemove(...pickedClassIds) : arrayUnion(...pickedClassIds),
      });
      alert(remove ? `✅ Removed from ${pickedClassIds.length} class(es).` : `✅ Online class now shows in ${pickedClassIds.length} selected class(es).`);
      loadOnline();
    } catch (e) { alert("❌ " + e.message); }
    finally { setBusy(false); }
  };

  const visible = classes.filter(c =>
    !filter || `${c.name} ${c.facultyName || ""} ${c.facultyEmail || ""}`.toLowerCase().includes(filter.toLowerCase()));
  const pickedClassIds = Object.keys(picked).filter(k => picked[k]);
  const contents = mode === "library"
    ? libItems.filter(f => libPicked[f.id]).map(f => ({ name: f.name, url: f.url, order: f.order, from: { class: libSource, fileId: f.id } }))
    : (name.trim() && url.trim() ? [{ name: name.trim(), url: url.trim(), from: null }] : []);

  const share = async () => {
    if (contents.length === 0) { alert("Choose at least one material (or enter a name and link)."); return; }
    if (pickedClassIds.length === 0) { alert("Choose at least one class."); return; }
    if (!window.confirm(`Share ${contents.length} item(s) with ${pickedClassIds.length} class(es)?`)) return;
    setBusy(true);
    try {
      // Skip items already present in a class (same URL + same section)
      const existing = new Set();
      for (const cid of pickedClassIds) {
        const snap = await getDocs(query(collection(db, "files"), where("facultyClassId", "==", cid)));
        snap.docs.forEach(d => { const f = d.data(); if ((f.section || "Reading Materials") === kind) existing.add(cid + "|" + f.url); });
      }
      const shareBatchId = "share_" + Date.now();
      const writes = [];
      pickedClassIds.forEach(cid => contents.forEach(c => {
        if (existing.has(cid + "|" + c.url)) return;
        writes.push({
          facultyClassId: cid, name: c.name, url: c.url, section: kind,
          ...(kind === "Assignments" ? { dueDate: dueDate || null } : {}),
          ...(c.from ? { sharedFrom: c.from } : {}),
          ...(typeof c.order === "number" ? { order: c.order } : {}),
          shareBatchId, addedBy: user?.email || "admin", createdAt: serverTimestamp(),
        });
      }));
      for (let i = 0; i < writes.length; i += 450) {
        const b = writeBatch(db);
        writes.slice(i, i + 450).forEach(w => b.set(doc(collection(db, "files")), w));
        await b.commit();
      }
      const skipped = pickedClassIds.length * contents.length - writes.length;
      alert(`✅ Added ${writes.length} item(s) across ${pickedClassIds.length} class(es).` + (skipped ? `\n${skipped} skipped (already in that class).` : ""));
      setLibPicked({}); setName(""); setUrl(""); setDueDate("");
      loadHistory();
    } catch (e) { alert("❌ " + e.message); }
    finally { setBusy(false); }
  };

  const undo = async (g) => {
    if (!window.confirm(`Remove this share from ${g.classIds.size} class(es)? (${g.ids.length} item(s))`)) return;
    for (let i = 0; i < g.ids.length; i += 450) {
      const b = writeBatch(db);
      g.ids.slice(i, i + 450).forEach(id => b.delete(doc(db, "files", id)));
      await b.commit();
    }
    loadHistory();
  };

  const classLabel = (id) => classes.find(c => c.id === id)?.name || "(deleted class)";

  return (
    <Section title="Share reading materials or assignments with selected classes" accent="#6a1b9a">
      {/* Step 1 — what */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <strong style={{ color: "#4a148c" }}>1. Type:</strong>
        <select value={kind} onChange={e => setKind(e.target.value)} style={inp}>
          <option value="Reading Materials">📖 Reading Materials</option>
          <option value="Assignments">📋 Assignments</option>
          <option value="Online Class">🎓 Online Class</option>
        </select>
        {!isOnline && <>
        <strong style={{ color: "#4a148c", marginLeft: 8 }}>Content:</strong>
        <select value={mode} onChange={e => setMode(e.target.value)} style={inp}>
          <option value="library">From course library</option>
          <option value="link">New link</option>
        </select>
        {mode === "library" && (
          <select value={libSource} onChange={e => setLibSource(e.target.value)} style={inp}>
            <option value="Engineering Physics">Engg. Physics</option>
            <option value="PUC-I">PUC-I</option>
            <option value="PUC-II">PUC-II</option>
          </select>
        )}
        </>}
        {kind === "Assignments" && (
          <>
            <strong style={{ color: "#4a148c", marginLeft: 8 }}>Due:</strong>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inp} />
          </>
        )}
      </div>

      {isOnline ? (
        <div style={{ border: "1px solid #e1bee7", borderRadius: 8, padding: 10, marginBottom: 14, background: "#fcf7fd" }}>
          {onlineList.length === 0 ? <span style={{ color: "#555" }}>No live or upcoming online classes. Schedule one on the 🎓 Online Class page first.</span>
            : onlineList.map(c => (
              <label key={c.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "5px 2px", cursor: "pointer" }}>
                <input type="radio" name="onlinePick" checked={onlinePick === c.id}
                  onChange={() => { setOnlinePick(c.id); const a = {}; (c.classIds || []).forEach(id => a[id] = true); setPicked(a); }} />
                <span style={{ color: "#1a237e", fontWeight: 700 }}>{c.isActive ? "🔴 LIVE: " : "🕒 "}{c.className}</span>
                <span style={{ color: "#607d8b", fontSize: "0.8rem" }}>
                  — {new Date(c.scheduledTime).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  {" · "}{(c.classIds || []).length ? `in ${c.classIds.length} class(es)` : "not shared yet (shows in all classes)"}
                </span>
              </label>
            ))}
          {onlinePick && <div style={{ color: "#6a1b9a", fontSize: "0.8rem", marginTop: 6 }}>The classes it is already in are pre-ticked below.</div>}
        </div>
      ) : mode === "library" ? (
        <div style={{ border: "1px solid #e1bee7", borderRadius: 8, padding: 10, marginBottom: 14, maxHeight: 220, overflowY: "auto", background: "#fcf7fd" }}>
          {libItems.length === 0 ? <span style={{ color: "#555" }}>No {kind.toLowerCase()} found on the {libSource} page.</span> : (
            <>
              <button style={{ ...btnSecondary, padding: "4px 10px", marginBottom: 6 }}
                onClick={() => { const a = {}; libItems.forEach(f => a[f.id] = true); setLibPicked(a); }}>Select all</button>
              {libItems.map(f => (
                <label key={f.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 2px", color: "#1a237e", cursor: "pointer" }}>
                  <input type="checkbox" checked={!!libPicked[f.id]} onChange={e => setLibPicked(p => ({ ...p, [f.id]: e.target.checked }))} />
                  📄 {f.name}
                </label>
              ))}
            </>
          )}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8, marginBottom: 14 }}>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Title" style={inp} />
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="Link (PDF / Drive / YouTube)" style={inp} />
        </div>
      )}

      {/* Step 2 — where */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        <strong style={{ color: "#4a148c" }}>2. Classes:</strong>
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter (e.g. CE, C2, faculty name)" style={{ ...inp, minWidth: 220 }} />
        <button style={btnSecondary} onClick={() => { const a = { ...picked }; visible.forEach(c => a[c.id] = true); setPicked(a); }}>Select shown</button>
        <button style={btnSecondary} onClick={() => setPicked({})}>Clear</button>
        <span style={{ color: "#4a148c", fontWeight: 700 }}>{pickedClassIds.length} selected</span>
      </div>
      <div style={{ border: "1px solid #e1bee7", borderRadius: 8, padding: 10, maxHeight: 260, overflowY: "auto", marginBottom: 14 }}>
        {visible.length === 0 ? <span style={{ color: "#555" }}>No classes found.</span> : visible.map(c => (
          <label key={c.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 2px", cursor: "pointer" }}>
            <input type="checkbox" checked={!!picked[c.id]} onChange={e => setPicked(p => ({ ...p, [c.id]: e.target.checked }))} />
            <span style={{ color: "#1a237e", fontWeight: 600 }}>{c.icon || "📘"} {c.name}</span>
            <span style={{ color: "#607d8b", fontSize: "0.8rem" }}>— {c.facultyName || c.facultyEmail}{c.batch ? ` · ${c.batch}` : ""}</span>
          </label>
        ))}
      </div>

      {isOnline ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => shareOnline(false)} disabled={busy} style={{ ...btnPrimary, background: "#6a1b9a" }}>
            {busy ? "Saving…" : `🎯 3. Show in ${pickedClassIds.length} selected class(es)`}
          </button>
          <button onClick={() => shareOnline(true)} disabled={busy} style={btnSecondary}>Remove from selected classes</button>
        </div>
      ) : (
      <button onClick={share} disabled={busy} style={{ ...btnPrimary, background: "#6a1b9a" }}>
        {busy ? "Sharing…" : `🎯 3. Share ${contents.length} item(s) → ${pickedClassIds.length} class(es)`}
      </button>
      )}

      <h4 style={{ color: "#4a148c", margin: "20px 0 8px", fontSize: "0.95rem" }}>Recent shares ({history.length})</h4>
      {history.length === 0 ? <p style={{ color: "#37474f" }}>Nothing shared yet.</p> : history.map(g => (
        <div key={g.id} style={rowStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "#1a237e", fontWeight: 700 }}>{g.section === "Assignments" ? "📋" : "📖"} {[...g.names].join(", ")}</div>
            <div style={{ color: "#37474f", fontSize: "0.78rem" }}>
              → {[...g.classIds].map(classLabel).join(", ")}{g.at ? ` · ${new Date(g.at * 1000).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}` : ""}
            </div>
          </div>
          <button onClick={() => undo(g)} style={btnDanger}>↩ Undo share</button>
        </div>
      ))}
    </Section>
  );
}

/* ───────────────────────── Reading Materials ───────────────────────── */
function BroadcastReading({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [level, setLevel] = useState("PUC-I");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "files"), where("broadcastLevel", "!=", null)));
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const push = async () => {
    if (!name.trim() || !url.trim()) { alert("Name and URL are required"); return; }
    setSaving(true);
    try {
      await addDoc(collection(db, "files"), {
        name: name.trim(),
        url: url.trim(),
        section: "Reading Materials",
        broadcastLevel: level,
        facultyClassId: null,
        addedBy: user?.email || "admin",
        createdAt: serverTimestamp(),
      });
      setName(""); setUrl("");
      load();
      alert(`✅ Broadcast pushed to all ${level === "All" ? "classes" : level + " classes"}.`);
    } catch (e) { alert("❌ " + e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this broadcast from every class?")) return;
    await deleteDoc(doc(db, "files", id));
    load();
  };

  return (
    <Section title="Push a reading material to many classes" accent="#0288d1">
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 2fr 1fr auto", gap: 8, marginBottom: 14 }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Material name (e.g. Newton's Laws notes)" style={inp} />
        <input value={url}  onChange={e => setUrl(e.target.value)}  placeholder="Link (PDF / Drive / YouTube)" style={inp} />
        <select value={level} onChange={e => setLevel(e.target.value)} style={inp}>
          {LEVELS.map(l => <option key={l} value={l}>{l === "All" ? "📡 All classes" : `📡 All ${l} classes`}</option>)}
        </select>
        <button onClick={push} disabled={saving} style={btnPrimary}>{saving ? "Pushing…" : "📡 Push"}</button>
      </div>

      <h4 style={{ color: "#01579b", margin: "16px 0 8px", fontSize: "0.95rem" }}>
        Current Broadcasts ({items.length})
      </h4>
      {loading ? <p style={{ color: "#546e7a" }}>⏳ Loading…</p>
        : items.length === 0 ? <p style={{ color: "#37474f", fontWeight: 500 }}>No broadcasts yet.</p>
        : items.map(f => {
          const meta = LEVEL_BG[f.broadcastLevel] || { bg: "#eee", color: "#333" };
          return (
            <div key={f.id} style={rowStyle}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <a href={f.url} target="_blank" rel="noreferrer"
                  style={{ color: "#1a237e", textDecoration: "none", fontWeight: 700 }}>📄 {f.name}</a>
                <div style={{ color: "#37474f", fontSize: "0.78rem", marginTop: 2, fontWeight: 500 }}>
                  {f.url}
                </div>
              </div>
              <span style={{ background: meta.bg, color: meta.color, borderRadius: 12, padding: "3px 10px", fontSize: "0.74rem", fontWeight: 700 }}>
                📡 {f.broadcastLevel}
              </span>
              <button onClick={() => remove(f.id)} style={btnDanger}>🗑 Remove</button>
            </div>
          );
        })}
    </Section>
  );
}

/* ───────────────────────── Quizzes ───────────────────────── */
function BroadcastQuiz({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [timeLimit, setTimeLimit] = useState(30);
  const [level, setLevel] = useState("PUC-I");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "quizzes"), where("broadcastLevel", "!=", null)));
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const push = async () => {
    if (!title.trim()) { alert("Title required"); return; }
    setSaving(true);
    try {
      await addDoc(collection(db, "quizzes"), {
        title: title.trim(),
        timeLimit: Number(timeLimit) || 30,
        questions: [],
        broadcastLevel: level,
        facultyClassId: null,
        assignedBatch: "Both",
        class: level === "All" ? "All" : level,
        createdBy: user?.email || "admin",
        createdByName: user?.displayName || "Admin",
        createdAt: serverTimestamp(),
      });
      setTitle(""); setTimeLimit(30);
      load();
      alert(`✅ Quiz shell created and broadcast to ${level === "All" ? "all classes" : "all " + level + " classes"}.\n\nNow go to Admin Panel → 📝 Quizzes to add questions and schedule it.`);
    } catch (e) { alert("❌ " + e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this broadcast quiz from every class?")) return;
    await deleteDoc(doc(db, "quizzes", id));
    load();
  };

  return (
    <Section title="Push a quiz to many classes" accent="#2e7d32">
      <p style={{ color: "#1c2833", fontWeight: 500, fontSize: "0.86rem", margin: "0 0 12px" }}>
        Create a broadcast quiz shell here. Then open <strong>📝 Quizzes</strong> in the Admin Panel to add questions
        (with LaTeX, images, scheduling, etc). The quiz will already be visible in every targeted class.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 100px 1fr auto", gap: 8, marginBottom: 14 }}>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Quiz title (e.g. Mid-term Test 1)" style={inp} />
        <input type="number" value={timeLimit} onChange={e => setTimeLimit(e.target.value)} placeholder="min" style={inp} />
        <select value={level} onChange={e => setLevel(e.target.value)} style={inp}>
          {LEVELS.map(l => <option key={l} value={l}>{l === "All" ? "📡 All classes" : `📡 All ${l} classes`}</option>)}
        </select>
        <button onClick={push} disabled={saving} style={btnPrimary}>{saving ? "Creating…" : "📡 Push Shell"}</button>
      </div>

      <h4 style={{ color: "#1b5e20", margin: "16px 0 8px", fontSize: "0.95rem" }}>
        Current Broadcast Quizzes ({items.length})
      </h4>
      {loading ? <p style={{ color: "#546e7a" }}>⏳ Loading…</p>
        : items.length === 0 ? <p style={{ color: "#37474f", fontWeight: 500 }}>No broadcast quizzes yet.</p>
        : items.map(q => {
          const meta = LEVEL_BG[q.broadcastLevel] || { bg: "#eee", color: "#333" };
          return (
            <div key={q.id} style={rowStyle}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: "#1a237e", fontWeight: 700 }}>📝 {q.title}</div>
                <div style={{ color: "#37474f", fontSize: "0.78rem", marginTop: 2, fontWeight: 500 }}>
                  {(q.questions || []).length} questions · {q.timeLimit || 30} min
                  {q.openUntil ? ` · closes ${new Date(q.openUntil).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}` : ""}
                </div>
              </div>
              <span style={{ background: meta.bg, color: meta.color, borderRadius: 12, padding: "3px 10px", fontSize: "0.74rem", fontWeight: 700 }}>
                📡 {q.broadcastLevel}
              </span>
              <button onClick={() => remove(q.id)} style={btnDanger}>🗑 Remove</button>
            </div>
          );
        })}
    </Section>
  );
}

/* ───────────────────────── Assignments ───────────────────────── */
function BroadcastAssignment({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [level, setLevel] = useState("PUC-I");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, "files"),
        where("broadcastLevel", "!=", null)
      ));
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(f => f.section === "Assignments")
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const push = async () => {
    if (!name.trim() || !url.trim()) { alert("Title and URL are required"); return; }
    setSaving(true);
    try {
      await addDoc(collection(db, "files"), {
        name: name.trim(),
        url: url.trim(),
        section: "Assignments",
        dueDate: dueDate || null,
        broadcastLevel: level,
        facultyClassId: null,
        addedBy: user?.email || "admin",
        createdAt: serverTimestamp(),
      });
      setName(""); setUrl(""); setDueDate("");
      load();
      alert(`✅ Assignment pushed to ${level === "All" ? "all classes" : "all " + level + " classes"}.`);
    } catch (e) { alert("❌ " + e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this assignment broadcast from every class?")) return;
    await deleteDoc(doc(db, "files", id));
    load();
  };

  return (
    <Section title="Push an assignment to many classes" accent="#e65100">
      <p style={{ color: "#1c2833", fontWeight: 500, fontSize: "0.86rem", margin: "0 0 12px" }}>
        Assignments appear in every targeted class's <strong>📋 Assignments</strong> tab — distinct from reading materials, with a due date.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 2fr 160px 1fr auto", gap: 8, marginBottom: 14 }}>
        <input value={name}  onChange={e => setName(e.target.value)}  placeholder="Assignment title" style={inp} />
        <input value={url}   onChange={e => setUrl(e.target.value)}   placeholder="Link (PDF / Drive instructions)" style={inp} />
        <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inp} />
        <select value={level} onChange={e => setLevel(e.target.value)} style={inp}>
          {LEVELS.map(l => <option key={l} value={l}>{l === "All" ? "📡 All" : `📡 ${l}`}</option>)}
        </select>
        <button onClick={push} disabled={saving} style={btnPrimary}>{saving ? "Pushing…" : "📡 Push"}</button>
      </div>

      <h4 style={{ color: "#bf360c", margin: "16px 0 8px", fontSize: "0.95rem" }}>
        Current Assignment Broadcasts ({items.length})
      </h4>
      {loading ? <p style={{ color: "#546e7a" }}>⏳ Loading…</p>
        : items.length === 0 ? <p style={{ color: "#37474f", fontWeight: 500 }}>No assignment broadcasts yet.</p>
        : items.map(f => {
          const meta = LEVEL_BG[f.broadcastLevel] || { bg: "#eee", color: "#333" };
          return (
            <div key={f.id} style={rowStyle}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <a href={f.url} target="_blank" rel="noreferrer"
                  style={{ color: "#1a237e", textDecoration: "none", fontWeight: 700 }}>📋 {f.name}</a>
                <div style={{ color: "#37474f", fontSize: "0.78rem", marginTop: 2, fontWeight: 500 }}>
                  {f.dueDate ? `📅 Due ${new Date(f.dueDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : "No due date"}
                </div>
              </div>
              <span style={{ background: meta.bg, color: meta.color, borderRadius: 12, padding: "3px 10px", fontSize: "0.74rem", fontWeight: 700 }}>
                📡 {f.broadcastLevel}
              </span>
              <button onClick={() => remove(f.id)} style={btnDanger}>🗑 Remove</button>
            </div>
          );
        })}
    </Section>
  );
}

/* ───────────────────────── Simulations ───────────────────────── */
function BroadcastSimulation({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("catalog"); // "catalog" | "custom"
  const [simId, setSimId] = useState(SIM_CATALOG[0]?.id || "");
  const [customTitle, setCustomTitle] = useState("");
  const [customUrl, setCustomUrl] = useState("");
  const [level, setLevel] = useState("PUC-I");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, "broadcastSimulations"),
        where("broadcastLevel", "!=", null),
      ));
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const push = async () => {
    setSaving(true);
    try {
      let payload = {};
      if (source === "catalog") {
        const sim = SIM_CATALOG.find(s => s.id === simId);
        if (!sim) { alert("Pick a simulation from the catalog."); setSaving(false); return; }
        payload = { simId: sim.id, title: sim.title, url: sim.url, source: "catalog" };
      } else {
        if (!customTitle.trim() || !customUrl.trim()) { alert("Title and URL are required for a custom simulation."); setSaving(false); return; }
        payload = { simId: null, title: customTitle.trim(), url: customUrl.trim(), source: "custom" };
      }
      await addDoc(collection(db, "broadcastSimulations"), {
        ...payload,
        broadcastLevel: level,
        addedBy: user?.email || "admin",
        createdAt: serverTimestamp(),
      });
      setCustomTitle(""); setCustomUrl("");
      load();
      alert(`✅ Simulation broadcast to ${level === "All" ? "all classes" : "all " + level + " classes"}.`);
    } catch (e) { alert("❌ " + e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this simulation broadcast from every class?")) return;
    await deleteDoc(doc(db, "broadcastSimulations", id));
    load();
  };

  return (
    <Section title="Push a simulation to many classes" accent="#6a1b9a">
      <p style={{ color: "#1c2833", fontWeight: 500, fontSize: "0.86rem", margin: "0 0 12px" }}>
        Pick from the existing simulation catalog OR paste any external link (PhET, your own HTML, YouTube, etc).
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <button onClick={() => setSource("catalog")} style={{
          ...((source === "catalog") ? btnPrimary : btnSecondary),
          padding: "7px 14px", fontSize: "0.82rem"
        }}>📚 From catalog</button>
        <button onClick={() => setSource("custom")} style={{
          ...((source === "custom") ? btnPrimary : btnSecondary),
          padding: "7px 14px", fontSize: "0.82rem"
        }}>🔗 Custom URL</button>
      </div>

      {source === "catalog" ? (
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 8, marginBottom: 14 }}>
          <select value={simId} onChange={e => setSimId(e.target.value)} style={inp}>
            {SIM_CATALOG.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          <select value={level} onChange={e => setLevel(e.target.value)} style={inp}>
            {LEVELS.map(l => <option key={l} value={l}>{l === "All" ? "📡 All" : `📡 ${l}`}</option>)}
          </select>
          <button onClick={push} disabled={saving} style={btnPrimary}>{saving ? "Pushing…" : "📡 Push"}</button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 2fr 1fr auto", gap: 8, marginBottom: 14 }}>
          <input value={customTitle} onChange={e => setCustomTitle(e.target.value)} placeholder="Simulation title" style={inp} />
          <input value={customUrl}   onChange={e => setCustomUrl(e.target.value)}   placeholder="https:// link" style={inp} />
          <select value={level} onChange={e => setLevel(e.target.value)} style={inp}>
            {LEVELS.map(l => <option key={l} value={l}>{l === "All" ? "📡 All" : `📡 ${l}`}</option>)}
          </select>
          <button onClick={push} disabled={saving} style={btnPrimary}>{saving ? "Pushing…" : "📡 Push"}</button>
        </div>
      )}

      <h4 style={{ color: "#4a148c", margin: "16px 0 8px", fontSize: "0.95rem" }}>
        Current Simulation Broadcasts ({items.length})
      </h4>
      {loading ? <p style={{ color: "#546e7a" }}>⏳ Loading…</p>
        : items.length === 0 ? <p style={{ color: "#37474f", fontWeight: 500 }}>No simulation broadcasts yet.</p>
        : items.map(s => {
          const meta = LEVEL_BG[s.broadcastLevel] || { bg: "#eee", color: "#333" };
          return (
            <div key={s.id} style={rowStyle}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <a href={s.url} target="_blank" rel="noreferrer"
                  style={{ color: "#1a237e", textDecoration: "none", fontWeight: 700 }}>🔬 {s.title}</a>
                <div style={{ color: "#37474f", fontSize: "0.78rem", marginTop: 2, fontWeight: 500 }}>
                  {s.source === "catalog" ? "From catalog" : "Custom link"} · {s.url}
                </div>
              </div>
              <span style={{ background: meta.bg, color: meta.color, borderRadius: 12, padding: "3px 10px", fontSize: "0.74rem", fontWeight: 700 }}>
                📡 {s.broadcastLevel}
              </span>
              <button onClick={() => remove(s.id)} style={btnDanger}>🗑 Remove</button>
            </div>
          );
        })}
    </Section>
  );
}

/* ── UI helpers ── */
const inp = { padding: "8px 10px", borderRadius: 7, border: "1.5px solid #c5cae9", fontSize: "0.88rem", outline: "none" };
const btnPrimary = { background: "#1a237e", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.88rem" };
const btnSecondary = { background: "#fff", color: "#1a237e", border: "1.5px solid #c5cae9", borderRadius: 8, padding: "9px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.88rem" };
const btnDanger = { background: "#fff", color: "#c62828", border: "1.5px solid #ef9a9a", borderRadius: 6, padding: "6px 11px", fontWeight: 700, cursor: "pointer", fontSize: "0.78rem" };
const rowStyle = {
  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
  padding: "10px 12px", background: "#fafbff", border: "1px solid #d6dcf0",
  borderRadius: 8, marginBottom: 6
};

function Section({ title, accent, children }) {
  return (
    <div style={{
      background: "#fff", border: "1.5px solid #c5cae9",
      borderTop: `5px solid ${accent}`, borderRadius: 10,
      padding: 18, marginBottom: 18,
      boxShadow: "0 3px 10px rgba(26,35,126,0.08)"
    }}>
      <h3 style={{ margin: "0 0 12px", color: accent, fontWeight: 800, fontSize: "1.02rem", paddingBottom: 8, borderBottom: `2px solid ${accent}22` }}>{title}</h3>
      {children}
    </div>
  );
}
